import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import { ApiError, useSnackbar } from '@jupi/ui'
import { useAircraft } from '../../api/queries'
import { useBookAircraft, type BookAircraftResult } from './api'

const toMinutes = (time: string) => {
  const match = /^(\d{1,2})[:.]?(\d{2})$/.exec(time.trim())
  return match ? Number(match[1]) * 60 + Number(match[2]) : null
}

// "CGX 14-18 every day from June to July", in one request rather than a
// booking per day. The dispatcher's tool; the bookings land in the main
// calendar like any other.
export default function BookAircraftDialog({ defaultDate, onClose }: { defaultDate: string; onClose: () => void }) {
  const { t } = useTranslation()
  const { showSnackbar } = useSnackbar()
  const { data: aircraft = [] } = useAircraft()
  const book = useBookAircraft()

  const [registration, setRegistration] = useState('')
  const [fromDate, setFromDate] = useState(defaultDate)
  const [toDate, setToDate] = useState(defaultDate)
  const [startTime, setStartTime] = useState('14:00')
  const [endTime, setEndTime] = useState('18:00')
  const [hours, setHours] = useState('')
  const [comment, setComment] = useState('')
  const [result, setResult] = useState<BookAircraftResult | null>(null)

  const start = toMinutes(startTime)
  const end = toMinutes(endTime)
  const windowHours = start !== null && end !== null && end > start ? (end - start) / 60 : null
  const flightHours = hours === '' ? (windowHours ?? 0) : Number(hours.replace(',', '.'))
  const valid =
    registration !== '' &&
    fromDate !== '' &&
    toDate >= fromDate &&
    windowHours !== null &&
    Number.isFinite(flightHours) &&
    flightHours >= 0 &&
    flightHours <= windowHours

  const submit = async () => {
    try {
      setResult(
        await book.mutateAsync({
          registration,
          fromDate,
          toDate,
          startTime,
          endTime,
          estimatedFlightHours: flightHours,
          comment: comment.trim() || null,
        }),
      )
    } catch (err) {
      showSnackbar(err instanceof ApiError ? err.message : t('common.error'), { severity: 'error' })
    }
  }

  const options = result?.created.filter((c) => c.kind === 'option').length ?? 0

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('firePatrol.bookAircraft')}</DialogTitle>
      <DialogContent>
        {result ? (
          <Stack spacing={1} sx={{ pt: 1 }}>
            <Alert severity="success">{t('firePatrol.bookDone', { count: result.created.length })}</Alert>
            {result.displaced.length > 0 ? <Alert severity="info">{t('firePatrol.bookDisplaced', { count: result.displaced.length })}</Alert> : null}
            {options > 0 ? <Alert severity="warning">{t('firePatrol.bookOptions', { count: options })}</Alert> : null}
            {result.skipped.length > 0 ? (
              <Alert severity="warning">{t('firePatrol.bookSkipped', { count: result.skipped.length })}</Alert>
            ) : null}
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="info">{t('firePatrol.bookHint')}</Alert>
            <TextField select label={t('firePatrol.aircraft')} value={registration} onChange={(e) => setRegistration(e.target.value)}>
              {aircraft.map((a) => (
                <MenuItem key={a.registration} value={a.registration}>
                  {a.registration}
                  {a.aircraftType ? ` · ${a.aircraftType}` : ''}
                </MenuItem>
              ))}
            </TextField>
            <Stack direction="row" spacing={2}>
              <TextField
                type="date"
                label={t('firePatrol.fromDate')}
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value)
                  if (toDate < e.target.value) setToDate(e.target.value)
                }}
                slotProps={{ inputLabel: { shrink: true } }}
                fullWidth
              />
              <TextField
                type="date"
                label={t('firePatrol.toDate')}
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: fromDate } }}
                fullWidth
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label={t('firePatrol.startTime')} value={startTime} onChange={(e) => setStartTime(e.target.value)} fullWidth />
              <TextField label={t('firePatrol.endTime')} value={endTime} onChange={(e) => setEndTime(e.target.value)} fullWidth />
            </Stack>
            <TextField
              label={t('firePatrol.estimatedHours')}
              value={hours}
              placeholder={windowHours === null ? '' : String(windowHours)}
              onChange={(e) => setHours(e.target.value)}
              slotProps={{ htmlInput: { inputMode: 'decimal' } }}
            />
            <TextField label={t('firePatrol.comment')} value={comment} onChange={(e) => setComment(e.target.value)} slotProps={{ htmlInput: { maxLength: 255 } }} />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('firePatrol.close')}</Button>
        {result ? null : (
          <Button variant="contained" onClick={submit} disabled={!valid || book.isPending}>
            {t('firePatrol.book')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
